/**
 * Validation for the public lead form.
 *
 * A lead with an unreachable phone number still gets assigned to a partner and
 * still costs them €3, so catching typos at the point of entry is worth more
 * than the small amount of friction it adds. Rules stay deliberately permissive:
 * the goal is to catch mistakes, never to reject a real customer.
 */

/** Strip spaces, dashes, dots, parens and a leading Greek country code. */
export function normalizeGreekPhone(input: string): string {
  let digits = input.replace(/[\s\-.()]/g, '');
  digits = digits.replace(/^\+30/, '').replace(/^0030/, '');
  return digits;
}

export interface ValidationResult {
  valid: boolean;
  /** Greek message shown under the field. */
  error?: string;
}

/**
 * Greek numbers are 10 digits: mobiles start with 69, landlines with 2.
 * Numbers written in international form (+xx…) are accepted as-is so customers
 * abroad, or with a foreign mobile, are never blocked.
 */
export function validatePhone(input: string): ValidationResult {
  const trimmed = input.trim();
  if (!trimmed) return { valid: false, error: 'Συμπληρώστε το τηλέφωνό σας.' };

  // Non-Greek international number — accept with a loose sanity check
  const compact = trimmed.replace(/[\s\-.()]/g, '');
  if (compact.startsWith('+') && !compact.startsWith('+30')) {
    return /^\+\d{8,15}$/.test(compact)
      ? { valid: true }
      : { valid: false, error: 'Ελέγξτε τον διεθνή αριθμό.' };
  }

  const digits = normalizeGreekPhone(trimmed);

  if (!/^\d+$/.test(digits)) {
    return { valid: false, error: 'Το τηλέφωνο πρέπει να περιέχει μόνο αριθμούς.' };
  }
  if (digits.length !== 10) {
    return {
      valid: false,
      error: digits.length < 10
        ? `Λείπουν ψηφία — το τηλέφωνο έχει 10 (δώσατε ${digits.length}).`
        : `Πολλά ψηφία — το τηλέφωνο έχει 10 (δώσατε ${digits.length}).`,
    };
  }
  if (!/^(6|2)/.test(digits)) {
    return { valid: false, error: 'Το τηλέφωνο ξεκινά με 69… (κινητό) ή 2… (σταθερό).' };
  }

  return { valid: true };
}

/** Greek postal codes are exactly 5 digits. */
export function validatePostalCode(input: string): ValidationResult {
  const digits = input.replace(/\s/g, '');
  if (!digits) return { valid: false, error: 'Συμπληρώστε τον ταχυδρομικό κώδικα.' };
  if (!/^\d{5}$/.test(digits)) {
    return { valid: false, error: 'Ο ΤΚ αποτελείται από 5 ψηφία (π.χ. 11527).' };
  }
  return { valid: true };
}

export function validateName(input: string): ValidationResult {
  const trimmed = input.trim();
  if (!trimmed) return { valid: false, error: 'Συμπληρώστε το όνομά σας.' };
  if (trimmed.length < 2) return { valid: false, error: 'Το όνομα είναι πολύ σύντομο.' };
  return { valid: true };
}
